import { BroadcastChannel } from 'broadcast-channel';
import Cookies from 'js-cookie';
import { languageChannelName } from '../constants/ChannelConstants';
import { defaultLanguage, languageDateStorageKey, Languages, languageStorageKey, } from '../constants/LanguageConstants';
import { UserLanguageKey } from '../constants/SettingConstants';
import { getEnvironment, getEnvironmentDomain, isLocal, isMSI, } from './EnvironmentUtil';
import { get, post, put, } from './RequestUtil';
import { getSetting } from './SettingUtil';
import { caseInsensitiveCompare } from './StringUtil';
let languageChannelOut = null;
function createLanguageChannel() {
    return new BroadcastChannel(languageChannelName);
}
function setCookieLocale(languageCode, env = getEnvironment()) {
    Cookies.set(`language-${env}`, languageCode, {
        domain: getEnvironmentDomain(env),
        expires: 60,
    });
}
function messageLanguageChannel(language) {
    if (!languageChannelOut) {
        languageChannelOut = createLanguageChannel();
    }
    languageChannelOut.postMessage(language);
}
export function validateLanguageCode(languageCode) {
    if (!languageCode) {
        return undefined;
    }
    return Object.keys(Languages).find(language => caseInsensitiveCompare(language, languageCode));
}
export function getCachedLanguage(env = getEnvironment()) {
    var _a, _b;
    return (_b = validateLanguageCode((_a = Cookies.get(`language-${env}`)) !== null && _a !== void 0 ? _a : localStorage.getItem(languageStorageKey))) !== null && _b !== void 0 ? _b : defaultLanguage;
}
export function getCachedLanguageWithDate(env = getEnvironment()) {
    var _a, _b;
    return {
        language: getCachedLanguage(env),
        date: (_b = (_a = Cookies.get(languageDateStorageKey)) !== null && _a !== void 0 ? _a : localStorage.getItem(languageDateStorageKey)) !== null && _b !== void 0 ? _b : '0',
    };
}
export function setLanguage(language, { env = getEnvironment(), shouldUpdateUserLanguage = false, } = {}) {
    var _a;
    const validLanguage = (_a = validateLanguageCode(language)) !== null && _a !== void 0 ? _a : defaultLanguage;
    messageLanguageChannel(validLanguage);
    localStorage.setItem(languageStorageKey, validLanguage);
    if (shouldUpdateUserLanguage) {
        const date = `${Date.now()}`;
        localStorage.setItem(languageDateStorageKey, date);
        Cookies.set(languageDateStorageKey, date, {
            domain: getEnvironmentDomain(env),
            expires: 60,
        });
    }
    setCookieLocale(validLanguage, env);
}
export async function getUserLanguage(organizationStandardUrl, token, partitionGlobalId, userId) {
    const settings = await get(`${organizationStandardUrl}/portal_/api/identity/Setting?key=${UserLanguageKey.Date}&key=${UserLanguageKey.Language}&partitionGlobalId=${partitionGlobalId}&userId=${userId}`, { token });
    const date = getSetting(settings, UserLanguageKey.Date);
    const language = getSetting(settings, UserLanguageKey.Language);
    if (!date || !language) {
        return null;
    }
    return {
        date,
        language,
    };
}
export async function setUserLanguage(language, organizationStandardUrl, token, partitionGlobalId, userId, { env = getEnvironment() } = {}) {
    var _a;
    // Clean up the cached language date which was used for the language comparison.
    localStorage.removeItem(languageDateStorageKey);
    Cookies.remove(languageDateStorageKey, { domain: getEnvironmentDomain(env) });
    const validLanguage = (_a = validateLanguageCode(language)) !== null && _a !== void 0 ? _a : defaultLanguage;
    messageLanguageChannel(validLanguage);
    const promises = [
        put(`${organizationStandardUrl}/portal_/api/identity/Setting`, {
            token,
            body: {
                settings: [
                    {
                        key: UserLanguageKey.Language,
                        value: validLanguage,
                    },
                    {
                        key: UserLanguageKey.Date,
                        value: `${Date.now()}`,
                    },
                ],
                partitionGlobalId,
                userId,
            },
        }),
    ];
    if (isMSI() && !isLocal()) {
        promises.push(post(`${organizationStandardUrl}/odata/Users/UiPath.Server.Configuration.OData.ChangeCulture`, {
            token,
            body: { culture: validLanguage },
        }));
    }
    await Promise.all(promises);
}
/**
 * @returns The disposer
 */
export function handleLanguageChange(callback) {
    const channel = createLanguageChannel();
    channel.onmessage = language => {
        callback(language);
    };
    return () => {
        channel.close();
    };
}
